// Wall framing math. Given a wall's length and height + stud spacing, return
// the stud / plate / header counts needed to frame it, plus framing nails.
//
// Standard residential framing assumptions baked in:
//   - Studs cut to (wall height − 4.5 in) to accommodate one bottom plate
//     (1.5 in) + double top plate (3 in). Pre-cut 92⅝" studs make exactly
//     97⅛" (8 ft + 1⅛") of framed wall.
//   - One stud at each end of the wall + one every `spacing` inches between.
//   - Each door/window opening adds 3 studs (king + 2 jacks) — a conservative
//     count that also covers cripples for typical openings.
//   - Top plate is doubled (load distribution), bottom plate is single, so
//     plate linear feet = wall length × 3.
//   - Headers: 1 per opening, ~4 ft of 2×6 each (handles typical 3 ft door /
//     window). Skip on non-load-bearing interior walls in practice — we still
//     include them so the buy list is conservative.
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export type StudSpacing = 16 | 24
export type StudSize = '2x4' | '2x6'

export interface LumberInputs {
  wallLengthFt: number
  wallHeightFt: number
  spacing: StudSpacing       // on-centre, inches
  studSize: StudSize
  doors: number
  windows: number
  wastePct: number           // 0–25
}

export interface LumberResult {
  studCount: number
  studLengthIn: number       // exact cut length each stud needs to be
  studPurchaseLengthFt: number  // nearest stock length to buy (8/10/12 ft)
  plateLinearFt: number      // total linear feet of plate lumber (top×2 + bottom×1)
  plateCount: number         // count of stock-length plates (rounded up)
  plateLengthFt: number      // stock length used for plate count
  headerCount: number        // count of openings needing headers
  headerLinearFt: number     // 4 ft of header per opening, by default
  totalBoardFt: number       // for reference / lumber-yard order
  nailLbs: number
  lines: AccessoryLine[]
}

export const DEFAULT_LUMBER: LumberInputs = {
  wallLengthFt: 12,
  wallHeightFt: 8,
  spacing: 16,
  studSize: '2x4',
  doors: 1,
  windows: 1,
  wastePct: 10,
}

const PLATES_PER_WALL = 3      // double top + single bottom
const PLATE_STOCK_FT = 12      // 12 ft stock is most common for plates
const HEADER_PER_OPENING_FT = 4  // conservative 4 ft of 2× header per opening
const NAILS_LBS_PER_STUD = 0.15  // ~1 lb / 6-7 studs for framing nails

function pickStockStudLength(neededIn: number): number {
  // Pre-cut studs at 92⅝" = 7.72 ft (8-ft wall with double top plate)
  // 8 ft = 96"  · 10 ft = 120" · 12 ft = 144"
  if (neededIn <= 92.625) return 92.625 / 12   // pre-cut stud
  if (neededIn <= 96) return 8
  if (neededIn <= 120) return 10
  if (neededIn <= 144) return 12
  return Math.ceil(neededIn / 24) * 2 / 1      // round up to nearest 2 ft
}

export function computeLumber(i: LumberInputs, locale: Locale = 'en'): LumberResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const wallLen = Math.max(0, i.wallLengthFt)
  const wallHeight = Math.max(0, i.wallHeightFt)
  const spacing = i.spacing
  const doors = Math.max(0, Math.round(i.doors))
  const windows = Math.max(0, Math.round(i.windows))
  const wastePct = Math.max(0, Math.min(50, i.wastePct))
  const openings = doors + windows

  // Stud cut length: wall height minus 4.5 in (one bottom plate 1.5" + double
  // top plate 3"). Rounded up to nearest pre-cut / stock length for buying.
  const studCutIn = Math.max(0, wallHeight * 12 - 4.5)
  const studPurchaseFt = wallHeight > 0 ? pickStockStudLength(studCutIn) : 0

  // Stud count = one at each end + one every `spacing` inches between.
  // Add 3 extra studs per opening (king + 2 jacks/cripples — conservative).
  const wallLenIn = wallLen * 12
  const baseStuds = wallLenIn > 0 ? Math.ceil(wallLenIn / spacing) + 1 : 0
  const openingStuds = openings * 3
  const studCount = Math.ceil((baseStuds + openingStuds) * (1 + wastePct / 100))

  // Plates: 3 plates per wall (top×2 + bottom×1) × wall length.
  const plateLinearFt = wallLen * PLATES_PER_WALL
  const plateCount = wallLen > 0 ? Math.max(1, Math.ceil((plateLinearFt * (1 + wastePct / 100)) / PLATE_STOCK_FT)) : 0

  // Headers: one per opening, ~4 ft of 2× material each.
  const headerCount = openings
  const headerLinearFt = headerCount * HEADER_PER_OPENING_FT

  // Board feet (informational): board ft = thickness(in) × width(in) × length(ft) / 12
  const studBdFt = (i.studSize === '2x6' ? 1.5 * 5.5 : 1.5 * 3.5) * studPurchaseFt * studCount / 12
  const plateBdFt = (i.studSize === '2x6' ? 1.5 * 5.5 : 1.5 * 3.5) * PLATE_STOCK_FT * plateCount / 12
  const headerBdFt = (1.5 * 5.5) * headerLinearFt / 12  // headers assume 2×6
  const totalBoardFt = Math.round(studBdFt + plateBdFt + headerBdFt)

  // Framing nails — sinker / common 16d for stud-to-plate, 8d for plate-to-floor.
  const nailLbs = Math.max(1, ceilTo(studCount * NAILS_LBS_PER_STUD, 1))

  const lines: AccessoryLine[] = []
  if (wallLen > 0 && wallHeight > 0) {
    lines.push({
      item: `${i.studSize} stud framing lumber kiln-dried`,
      label: tr(`Studs (${i.studSize}, ${studPurchaseFt} ft)`, `Montants (${i.studSize}, ${studPurchaseFt} pi)`),
      kind: 'consumable',
      qty: studCount,
      unit: 'piece',
      note: tr(
        `${studCount} studs at ${studPurchaseFt} ft · cut to ${round(studCutIn, 1)} in · ${spacing} in OC · includes ${openings * 3} for openings + ${wastePct}% waste`,
        `${studCount} montants de ${studPurchaseFt} pi · coupés à ${round(studCutIn, 1)} po · ${spacing} po centre à centre · inclut ${openings * 3} pour les ouvertures + ${wastePct} % de perte`,
      ),
    })
    lines.push({
      item: `${i.studSize} plate framing lumber kiln-dried`,
      label: tr(`Plates (${i.studSize}, ${PLATE_STOCK_FT} ft)`, `Lisses (${i.studSize}, ${PLATE_STOCK_FT} pi)`),
      kind: 'consumable',
      qty: plateCount,
      unit: 'piece',
      note: tr(
        `${plateCount} × ${PLATE_STOCK_FT} ft pieces · ${round(plateLinearFt, 1)} lin ft needed for double top + single bottom plate`,
        `${plateCount} pièces de ${PLATE_STOCK_FT} pi · ${round(plateLinearFt, 1)} pi linéaires pour double lisse haute + lisse basse simple`,
      ),
    })
    if (headerCount > 0) {
      lines.push({
        item: '2x6 header framing lumber kiln-dried',
        label: tr('Headers (2×6)', 'Linteaux (2×6)'),
        kind: 'consumable',
        qty: headerCount,
        unit: 'piece',
        note: tr(
          `~${HEADER_PER_OPENING_FT} ft of 2×6 per door/window opening — load-bearing walls only; skip on non-load-bearing interior walls`,
          `~${HEADER_PER_OPENING_FT} pi de 2×6 par ouverture de porte/fenêtre — murs porteurs seulement; à omettre sur murs intérieurs non porteurs`,
        ),
        optional: true,
      })
    }
    lines.push({
      item: 'framing nails 16d 3-1/2 galvanized',
      label: tr('Framing nails (16d)', 'Clous de charpente (16d)'),
      kind: 'consumable',
      qty: nailLbs,
      unit: 'lb',
      note: tr(
        `~${NAILS_LBS_PER_STUD * 1000 / 1000} lb per stud · 16d sinkers / commons for stud-to-plate connections`,
        `~${NAILS_LBS_PER_STUD * 1000 / 1000} lb par montant · clous 16d pour les connexions montant-lisse`,
      ),
    })
    // Tools — buy-once items frequently missing from a DIY toolbox
    lines.push({
      item: 'framing nailer pneumatic 21 degree',
      label: tr('Framing nailer', 'Cloueuse de charpente'),
      kind: 'tool',
      optional: true,
      note: tr('A pneumatic framer dramatically speeds up wall framing — rent for a weekend or buy if you frame more than one room.', 'Une cloueuse pneumatique accélère énormément la pose de mur — louez pour un weekend ou achetez si vous montez plus d’une pièce.'),
    })
    lines.push({
      item: 'framing square carpenter steel',
      label: tr('Framing square', 'Équerre de charpentier'),
      kind: 'tool',
      note: tr('Marks stud layout and squares wall corners — essential for any framing job.', 'Trace la disposition des montants et équerre les coins de mur — essentiel pour tout travail de charpente.'),
    })
    lines.push({
      item: 'long level 4 foot construction',
      label: tr('4-ft level', 'Niveau de 4 pi'),
      kind: 'tool',
      note: tr('Confirms walls are plumb before nailing top plate — short levels lie.', 'Confirme que les murs sont d’aplomb avant de clouer la sablière — les niveaux courts mentent.'),
    })
    lines.push({
      item: 'circular saw 7-1/4 inch worm drive',
      label: tr('Circular saw', 'Scie circulaire'),
      kind: 'tool',
      optional: true,
      note: tr('For trimming studs to length — skip if you buy pre-cut 92⅝" studs and your wall is exactly 8 ft.', 'Pour couper les montants à la longueur — à éviter si vous achetez des montants pré-coupés de 92⅝ po et que votre mur fait exactement 8 pi.'),
    })
  }

  return {
    studCount,
    studLengthIn: round(studCutIn, 1),
    studPurchaseLengthFt: round(studPurchaseFt, 3),
    plateLinearFt: round(plateLinearFt, 1),
    plateCount,
    plateLengthFt: PLATE_STOCK_FT,
    headerCount,
    headerLinearFt,
    totalBoardFt,
    nailLbs,
    lines,
  }
}
